import React, { useState, useRef, useEffect } from 'react';
import { X, Send, RotateCcw, Bot, CheckCheck, Tag, FileText, Menu, ListFilter } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { resolveDynamicVariables, getDynamicListItems } from './dynamicVariables';

export const SimulatorDrawer = ({ flowId, tenantId, csrfToken, nodes = [], edges = [], liveCollections = {}, onClose, onOpenRealTest }) => {
  const { isDark } = useTheme();

  const isFallbackFlow = React.useMemo(() => {
    const triggerNode = nodes.find((n) => n.type === 'triggerNode');
    return triggerNode?.data?.triggerType === 'FALLBACK';
  }, [nodes]);

  const triggerKeywords = React.useMemo(() => {
    if (isFallbackFlow) {
      return ['need help', 'track my order', 'pricing details'];
    }
    const triggerNode = nodes.find((n) => n.type === 'triggerNode');
    if (triggerNode?.data?.keywords) {
      const kws = Array.isArray(triggerNode.data.keywords)
        ? triggerNode.data.keywords
        : String(triggerNode.data.keywords).split(',').map((k) => k.trim());
      const filtered = kws.filter(Boolean);
      if (filtered.length > 0) return filtered;
    }
    return ['hi', 'hello'];
  }, [nodes, isFallbackFlow]);

  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      text: isFallbackFlow
        ? '🛡️ Test Simulator Ready! This is a Default Fallback Flow. Send any message below to test how the bot responds when no keywords match.'
        : '👋 Test Simulator Ready! Type "hi" or one of your trigger keywords to start the flow.',
      time: 'Just now',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTags, setActiveTags] = useState([]);
  const [activeListSheet, setActiveListSheet] = useState(null);
  const [simulatedVars, setSimulatedVars] = useState({});
  const [activeBranchId, setActiveBranchId] = useState(null);
  const [lastNodeId, setLastNodeId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const handleSendMessage = async (textToSend, btnPayload = null, extraContext = null) => {
    const text = (textToSend || input).trim();
    if (!text && !btnPayload) return;

    setInput('');

    // Update simulated session variables
    const updatedVars = { ...simulatedVars };
    if (extraContext && typeof extraContext === 'object') {
      Object.assign(updatedVars, extraContext);
      setSimulatedVars(updatedVars);
    }

    // Append user message to chat feed
    const newMsg = {
      sender: 'user',
      text: text,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, newMsg]);
    setLoading(true);

    // Determine active branch context (from user selection or simulated dropdown)
    const currentBranchId = updatedVars['selected_branch_id'] || activeBranchId;
    const currentBranch = (liveCollections?.branches || []).find((b) => b.id === currentBranchId) || liveCollections?.branches?.[0];

    // Prepare simulation context strictly from real Firestore data and user session variables (no mock/fallback strings)
    const fullContext = { ...updatedVars };

    if (currentBranch?.name || currentBranch?.title) {
      fullContext['branch.name'] = currentBranch.name || currentBranch.title;
      fullContext['branch.id'] = currentBranch.id;
      fullContext['branch.address'] = currentBranch.address || '';
      fullContext['branch.phone'] = currentBranch.phone || '';
      fullContext['member.branch_name'] = currentBranch.name || currentBranch.title;
    }

    // Filter live entities to current branch if available
    const liveClassesFiltered = currentBranchId
      ? (liveCollections?.classes || []).filter((c) => !c.branch_ids || c.branch_ids.length === 0 || c.branch_ids.includes(currentBranchId))
      : (liveCollections?.classes || []);
    const liveClass = liveClassesFiltered[0] || liveCollections?.classes?.[0];

    const livePkgsFiltered = currentBranchId
      ? (liveCollections?.packages || []).filter((p) => !p.branch_id || p.branch_id === currentBranchId || p.branch_id === 'all')
      : (liveCollections?.packages || []);
    const livePkg = livePkgsFiltered[0] || liveCollections?.packages?.[0];

    const livePrdsFiltered = currentBranchId
      ? (liveCollections?.products || []).filter((p) => !p.branch_id || p.branch_id === currentBranchId || p.branch_id === 'all')
      : (liveCollections?.products || []);
    const livePrd = livePrdsFiltered[0] || liveCollections?.products?.[0];

    if (liveClass?.title) {
      fullContext['class.class_name'] = liveClass.title;
      if (liveClass.description) fullContext['class.class_time'] = liveClass.description;
      if (liveClass.trainer_name) fullContext['class.trainer_name'] = liveClass.trainer_name;
      if (liveClass.room_name) fullContext['class.room_name'] = liveClass.room_name;
    }

    if (livePkg?.title) {
      fullContext['package.package_name'] = livePkg.title;
      if (livePkg.description) fullContext['package.package_price'] = livePkg.description;
    }

    if (livePrd?.title) {
      fullContext['product.product_name'] = livePrd.title;
      if (livePrd.description) fullContext['product.price'] = livePrd.description;
    }

    // Client-side Flow simulation (no obsolete backend endpoints)
    setTimeout(() => {
      try {
        const triggerNode = nodes.find((n) => n.type === 'triggerNode');
        let nextNode = null;

        if (btnPayload) {
          // Match button handle, list handle or raw node
          const matchingEdge = edges.find((ed) => 
            ed.sourceHandle === btnPayload || 
            ed.sourceHandle === `btn_${btnPayload}` || 
            ed.sourceHandle === `list_${btnPayload}` || 
            ed.source === btnPayload
          );
          if (matchingEdge) {
            nextNode = nodes.find((n) => n.id === matchingEdge.target);
          }
        }

        // If no direct branching match but we had a last node, find its output edge
        if (!nextNode && lastNodeId) {
          const outEdge = edges.find((ed) => ed.source === lastNodeId && (ed.sourceHandle === 'output' || !ed.sourceHandle));
          if (outEdge) {
            nextNode = nodes.find((n) => n.id === outEdge.target);
          }
        }

        // Initial trigger entry
        if (!nextNode) {
          const firstEdge = edges.find((ed) => ed.source === triggerNode?.id);
          if (firstEdge) {
            nextNode = nodes.find((n) => n.id === firstEdge.target);
          }
        }

        if (nextNode) {
          setLastNodeId(nextNode.id);
          const rawBody = nextNode.data?.body || nextNode.data?.question || nextNode.data?.prompt || nextNode.data?.label || '';
          const resolvedBody = resolveDynamicVariables(rawBody, fullContext);
          const nextButtons = nextNode.data?.buttons || (nextNode.data?.dateOptions ? nextNode.data.dateOptions.map((o) => ({ title: o, text: o })) : []);

          const isList = nextNode.data?.interactiveType === 'list';
          const nodeBranchScope = nextNode.data?.branchScope;
          const effectiveBranchId = (nodeBranchScope && nodeBranchScope !== 'dynamic')
            ? (nodeBranchScope === 'all' ? null : nodeBranchScope)
            : currentBranchId;

          const listItems = isList
            ? getDynamicListItems(nextNode.data?.listSource || 'packages', nextNode.data?.listCustomItems || [], liveCollections, effectiveBranchId)
            : [];

          const listConfig = isList
            ? {
                ctaText: nextNode.data?.listCtaText || (nextNode.data?.listSource === 'packages' ? 'View Packages' : 'Select Option'),
                sectionTitle: nextNode.data?.listSectionTitle || 'Available Options',
                variable: nextNode.data?.listVariable || 'selected_package',
                source: nextNode.data?.listSource || 'packages',
                items: listItems,
                nodeId: nextNode.id,
              }
            : null;

          setMessages((prev) => [
            ...prev,
            {
              sender: 'bot',
              text: resolvedBody,
              buttons: isList ? [] : nextButtons,
              interactiveType: isList ? 'list' : 'buttons',
              listConfig: listConfig,
              mediaUrl: nextNode.data?.mediaUrl,
              headerType: nextNode.data?.headerType,
              carouselCards: nextNode.data?.carousel_cards,
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              sender: 'bot',
              text: `Received: "${text}". Connect more blocks to your trigger to continue the flow!`,
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            },
          ]);
        }
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const handleReset = () => {

    setMessages([
      {
        sender: 'bot',
        text: '🔄 Session reset! Send "hi" or click a quick trigger below to start fresh.',
        time: 'Just now',
      },
    ]);
    setActiveTags([]);
    setSimulatedVars({});
    setActiveBranchId(null);
    setLastNodeId(null);
    setActiveListSheet(null);
  };

  return (
    <div
      className={`flow-simulator-drawer ${isDark ? 'dark' : ''}`}
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: 50,
        width: 380,
        minWidth: 380,
        maxWidth: 380,
        backgroundColor: isDark ? '#0c1317' : '#efeae2',
        borderLeft: isDark ? '1px solid #1e293b' : '1px solid #cbd5e1',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: '-6px 0 24px rgba(0, 0, 0, 0.18)',
        overflow: 'hidden',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Phone Header */}
      <div
        style={{
          display: 'flex',
          height: '60px',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: isDark ? '1px solid #1e293b' : '1px solid #cbd5e1',
          padding: '0 14px',
          backgroundColor: isDark ? '#1f2c34' : '#f0f2f5',
          color: isDark ? '#ffffff' : '#1e293b',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', height: '36px', width: '36px', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <Bot className="h-5 w-5" />
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, lineHeight: 1.2 }}>WhatsApp Simulator</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#10b981', fontWeight: 600 }}>
              <span style={{ display: 'inline-block', height: '6px', width: '6px', borderRadius: '50%', backgroundColor: '#10b981' }} />
              <span>Flow Test Active</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {onOpenRealTest && (
            <button
              onClick={onOpenRealTest}
              title="Test with Real WhatsApp Message"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                fontSize: '11px',
                fontWeight: 600,
                borderRadius: '6px',
                border: '1px solid #a7f3d0',
                backgroundColor: '#ecfdf5',
                color: '#047857',
                cursor: 'pointer',
              }}
            >
              <Send className="h-3 w-3" />
              <span>Real Test</span>
            </button>
          )}
          <button
            onClick={handleReset}
            title="Reset Conversation"
            style={{
              display: 'flex',
              height: '32px',
              width: '32px',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              border: 'none',
              background: 'transparent',
              color: '#64748b',
              cursor: 'pointer',
            }}
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            title="Close Simulator"
            style={{
              display: 'flex',
              height: '32px',
              width: '32px',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              border: 'none',
              background: 'transparent',
              color: '#64748b',
              cursor: 'pointer',
            }}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Simulated Branch Selector Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 14px',
          borderBottom: isDark ? '1px solid #1e293b' : '1px solid #cbd5e1',
          fontSize: '11px',
          backgroundColor: isDark ? '#111b21' : '#ffffff',
          color: isDark ? '#cbd5e1' : '#334155',
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Simulate Branch:
        </span>
        <select
          value={activeBranchId || 'all'}
          onChange={(e) => {
            const bVal = e.target.value === 'all' ? null : e.target.value;
            setActiveBranchId(bVal);
            const foundBranch = (liveCollections?.branches || []).find((b) => b.id === bVal);
            if (foundBranch) {
              setSimulatedVars((prev) => ({
                ...prev,
                selected_branch_id: foundBranch.id,
                'branch.name': foundBranch.name || foundBranch.title,
                'branch.id': foundBranch.id,
                'branch.address': foundBranch.address || '',
                'branch.phone': foundBranch.phone || '',
                'member.branch_name': foundBranch.name || foundBranch.title,
              }));
            } else {
              setSimulatedVars((prev) => {
                const next = { ...prev };
                delete next.selected_branch_id;
                delete next['branch.name'];
                delete next['branch.id'];
                delete next['branch.address'];
                delete next['branch.phone'];
                delete next['member.branch_name'];
                return next;
              });
            }
          }}
          className={`text-[11px] font-medium rounded px-2 py-0.5 border focus:outline-none focus:border-emerald-500 max-w-[210px] truncate ${
            isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300 text-slate-800'
          }`}
        >
          <option value="all">🌐 All Branches (Simulate)</option>
          {(liveCollections?.branches || []).map((br) => (
            <option key={br.id} value={br.id}>
              📍 {br.name || br.title}
            </option>
          ))}
        </select>
      </div>

      {/* Active Tags Bar */}
      {activeTags.length > 0 && (
        <div className={`flex items-center gap-1.5 px-3 py-1.5 border-b text-[10px] overflow-x-auto ${
          isDark ? 'bg-slate-900/90 border-slate-800 text-slate-400' : 'bg-white border-slate-200 text-slate-600'
        }`}>
          <Tag className="h-3 w-3 text-emerald-500 flex-shrink-0" />
          <span className={`font-semibold flex-shrink-0 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Contact Tags:</span>
          {activeTags.map((tag, i) => (
            <span
              key={i}
              className={`rounded px-1.5 py-0.2 whitespace-nowrap border ${
                isDark
                  ? 'bg-emerald-950/70 border-emerald-800/60 text-emerald-300'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-800 font-semibold'
              }`}
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Chat Messages Feed */}
      <div 
        className={`flow-simulator-drawer-feed ${isDark ? 'dark' : ''} space-y-3`}
        style={{
          backgroundColor: isDark ? '#0c1317' : '#efeae2',
          backgroundImage: isDark
            ? 'radial-gradient(#1e293b 1.2px, #0c1317 1.2px)'
            : 'radial-gradient(#cbd5e1 1.2px, #efeae2 1.2px)',
          backgroundSize: '16px 16px',
        }}
      >
        {messages.map((msg, idx) => {
          if (msg.sender === 'system') {
            return (
              <div key={idx} className="flex justify-center my-2">
                <span className={`rounded-full border px-3 py-1 text-[11px] shadow ${
                  isDark
                    ? 'bg-slate-800/90 border-slate-700/60 text-slate-300'
                    : 'bg-white border-slate-200 text-slate-600'
                }`}>
                  {msg.text}
                </span>
              </div>
            );
          }

          const isUser = msg.sender === 'user';

          return (
            <div key={idx} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 shadow-md text-xs leading-relaxed ${
                  isUser
                    ? (isDark ? 'bg-[#005c4b] text-emerald-50 rounded-tr-xs' : 'bg-[#d9fdd3] text-slate-800 rounded-tr-xs')
                    : (isDark ? 'bg-[#202c33] text-slate-100 rounded-tl-xs border border-slate-700/50' : 'bg-white text-slate-800 rounded-tl-xs border border-slate-200')
                }`}
              >
                {msg.mediaUrl && (
                  <div className="mb-2 rounded-lg overflow-hidden border border-black/10">
                    {msg.headerType === 'VIDEO' ? (
                      <video src={msg.mediaUrl} controls className="w-full max-h-40 rounded bg-black" />
                    ) : msg.headerType === 'DOCUMENT' ? (
                      <div className="flex items-center gap-2 p-2 bg-black/10 rounded text-xs font-mono">
                        <FileText className="h-4 w-4 text-emerald-500 shrink-0" />
                        <span className="truncate">{msg.mediaUrl}</span>
                      </div>
                    ) : (
                      <img src={msg.mediaUrl} alt="Header Media" className="w-full max-h-44 object-cover rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                  </div>
                )}
                <div className="whitespace-pre-wrap">{msg.text}</div>

                {/* WhatsApp Carousel Cards Deck */}
                {msg.carouselCards && msg.carouselCards.length > 0 && (
                  <div className="mt-2.5 pt-2 border-t border-slate-500/20">
                    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
                      {msg.carouselCards.map((c, ci) => {
                        const cMedia = c.header_url || c.media_url || 'https://picsum.photos/300/180';
                        return (
                          <div key={ci} className={`min-w-[150px] max-w-[150px] rounded-xl overflow-hidden border shadow-sm shrink-0 ${
                            isDark ? 'bg-[#111b21] border-slate-700' : 'bg-slate-50 border-slate-200'
                          }`}>
                            {cMedia && (
                              <div className="h-20 w-full overflow-hidden bg-black/20">
                                <img src={cMedia} alt={`Card ${ci + 1}`} className="h-full w-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                              </div>
                            )}
                            <div className="p-2 space-y-1.5">
                              <div className="text-[11px] font-medium leading-snug line-clamp-2 text-slate-800 dark:text-slate-100">
                                {c.body || `Card ${ci + 1}`}
                              </div>
                              {Array.isArray(c.buttons) && c.buttons.map((b, bi) => (
                                <button
                                  key={bi}
                                  type="button"
                                  onClick={() => handleSendMessage(b.text || b.title)}
                                  className="w-full text-center text-[10px] font-semibold py-1 px-1 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500 hover:text-white transition-colors truncate"
                                >
                                  {b.text || b.title}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${
                  isUser
                    ? (isDark ? 'text-emerald-200/70' : 'text-emerald-700')
                    : (isDark ? 'text-slate-400' : 'text-slate-500')
                }`}>
                  <span>{msg.time}</span>
                  {isUser && <CheckCheck className="h-3 w-3 text-emerald-500" />}
                </div>
              </div>

              {/* Bot Interactive Buttons */}
              {!isUser && msg.buttons && msg.buttons.length > 0 && (
                <div className="mt-1.5 flex flex-col gap-1 w-[85%]">
                  {msg.buttons.map((btn) => (
                    <button
                      key={btn.id}
                      onClick={() => handleSendMessage(btn.title, btn.id)}
                      disabled={loading}
                      className={`w-full rounded-xl border py-2 px-3 text-center text-xs font-semibold transition-all shadow-sm active:scale-98 ${
                        isDark
                          ? 'border-emerald-500/40 bg-[#111b21] text-emerald-400 hover:bg-emerald-600 hover:text-white'
                          : 'border-emerald-500/40 bg-white text-emerald-700 hover:bg-emerald-50'
                      }`}
                    >
                      {btn.title}
                    </button>
                  ))}
                </div>
              )}

              {/* Bot WhatsApp Interactive List CTA Button */}
              {!isUser && msg.interactiveType === 'list' && msg.listConfig && (
                <div className="mt-1.5 w-[85%]">
                  <button
                    type="button"
                    onClick={() => setActiveListSheet(msg.listConfig)}
                    disabled={loading}
                    className={`w-full rounded-xl border py-2.5 px-3 flex items-center justify-between text-xs font-bold transition-all shadow-sm active:scale-98 ${
                      isDark
                        ? 'border-teal-500/50 bg-[#111b21] text-teal-400 hover:bg-teal-600 hover:text-white'
                        : 'border-teal-500/50 bg-white text-teal-700 hover:bg-teal-50 shadow-xs'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <Menu className="h-4 w-4 text-teal-500" />
                      <span>{msg.listConfig.ctaText || 'View Packages'}</span>
                    </span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-teal-500/20 text-teal-400 font-mono">
                      {(msg.listConfig.items || []).length} items
                    </span>
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {loading && (
          <div className={`flex items-center gap-2 rounded-2xl px-3.5 py-2 text-xs w-fit ${
            isDark ? 'bg-[#202c33] text-slate-400' : 'bg-white text-slate-600 border border-slate-200'
          }`}>
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-ping" />
            <span>Bot is typing...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Quick Triggers */}
      <div
        style={{
          padding: '8px 12px',
          borderTop: isDark ? '1px solid #1e293b' : '1px solid #cbd5e1',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          overflowX: 'auto',
          fontSize: '11px',
          backgroundColor: isDark ? '#111b21' : '#f0f2f5',
        }}
      >
        <span style={{ fontSize: '10px', paddingLeft: '4px', color: '#64748b' }}>Quick:</span>
        {triggerKeywords.map((kw) => (
          <button
            key={kw}
            onClick={() => handleSendMessage(kw)}
            style={{
              borderRadius: '20px',
              border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
              padding: '2px 10px',
              whiteSpace: 'nowrap',
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              color: isDark ? '#e2e8f0' : '#334155',
              cursor: 'pointer',
              fontSize: '11px',
            }}
          >
            "{kw}"
          </button>
        ))}
      </div>

      {/* Message Input Bar */}
      <div
        style={{
          padding: '10px 12px',
          borderTop: isDark ? '1px solid #1e293b' : '1px solid #cbd5e1',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: isDark ? '#1f2c34' : '#f0f2f5',
        }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
          placeholder="Type 'hi' or a keyword..."
          style={{
            flex: 1,
            borderRadius: '20px',
            padding: '8px 16px',
            fontSize: '12px',
            backgroundColor: isDark ? '#2a3942' : '#ffffff',
            color: isDark ? '#ffffff' : '#0f172a',
            border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
            outline: 'none',
          }}
        />
        <button
          onClick={() => handleSendMessage()}
          disabled={loading || !input.trim()}
          style={{
            display: 'flex',
            height: '34px',
            width: '34px',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            backgroundColor: '#059669',
            color: '#ffffff',
            border: 'none',
            cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
            opacity: loading || !input.trim() ? 0.4 : 1,
            flexShrink: 0,
            transition: 'background-color 0.15s ease',
          }}
        >
          <Send className="h-4 w-4" />
        </button>
      </div>

      {/* WhatsApp Native Interactive List Bottom Sheet Modal */}
      {activeListSheet && (
        <div 
          onClick={() => setActiveListSheet(null)}
          className="absolute inset-0 z-50 flex flex-col justify-end bg-black/60 backdrop-blur-2xs transition-all animate-in fade-in duration-150"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-h-[75%] rounded-t-2xl flex flex-col shadow-2xl border-t transition-all ${
              isDark ? 'bg-[#1f2c34] border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-800'
            }`}
          >
            {/* Sheet Handle */}
            <div className="pt-2.5 pb-1 flex justify-center">
              <div className={`h-1 w-10 rounded-full ${isDark ? 'bg-slate-600' : 'bg-slate-300'}`} />
            </div>

            {/* Sheet Title */}
            <div className={`px-4 py-2.5 flex items-center justify-between border-b ${
              isDark ? 'border-slate-700' : 'border-slate-100'
            }`}>
              <div className="flex items-center gap-2">
                <ListFilter className="h-4 w-4 text-teal-500" />
                <span className="font-bold text-xs truncate max-w-[210px]">
                  {activeListSheet.sectionTitle || 'Available Options'}
                </span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded font-mono font-semibold ${
                  isDark ? 'bg-teal-500/20 text-teal-300' : 'bg-teal-50 text-teal-700'
                }`}>
                  {(activeListSheet.items || []).length} items
                </span>
              </div>
              <button
                type="button"
                onClick={() => setActiveListSheet(null)}
                className={`rounded-full p-1 transition-colors ${
                  isDark ? 'text-slate-400 hover:bg-slate-700 hover:text-white' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* List Rows (up to 10 rows) */}
            <div className="overflow-y-auto p-2 space-y-1 divide-y divide-slate-700/20 max-h-[350px]">
              {(activeListSheet.items || []).length === 0 ? (
                <div className={`p-4 text-center text-xs italic ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {activeListSheet.source === 'custom'
                    ? 'No custom options configured.'
                    : `No active ${activeListSheet.source} found in the database for this gym.`}
                </div>
              ) : (
                (activeListSheet.items || []).map((item, idx) => (
                  <button
                    key={item.id || idx}
                    type="button"
                    onClick={() => {
                      const targetPayload = item.id;
                      const titleToSend = item.title;
                      const sheetVar = activeListSheet.variable;
                      const extra = { [sheetVar]: titleToSend, ...item };
                      if (activeListSheet.source === 'branches') {
                        extra.selected_branch_id = item.id;
                        extra['branch.name'] = item.name || item.title;
                        extra['branch.id'] = item.id;
                        if (item.address) extra['branch.address'] = item.address;
                        if (item.phone) extra['branch.phone'] = item.phone;
                        extra['member.branch_name'] = item.name || item.title;
                        setActiveBranchId(item.id);
                      }
                      setActiveListSheet(null);
                      handleSendMessage(titleToSend, targetPayload, extra);
                    }}
                    className={`w-full text-left p-2.5 rounded-xl flex items-center justify-between transition-all group ${
                      isDark
                        ? 'hover:bg-slate-800/90 active:bg-teal-950/40'
                        : 'hover:bg-slate-50 active:bg-teal-50'
                    }`}
                  >
                    <div className="min-w-0 pr-3">
                      <div className="font-semibold text-xs leading-tight group-hover:text-teal-400 transition-colors">
                        {item.title}
                      </div>
                      {item.description && (
                        <div className={`text-[11px] mt-0.5 line-clamp-1 ${
                          isDark ? 'text-slate-400' : 'text-slate-500'
                        }`}>
                          {item.description}
                        </div>
                      )}
                    </div>
                    <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      isDark
                        ? 'border-slate-600 group-hover:border-teal-400'
                        : 'border-slate-300 group-hover:border-teal-600'
                    }`}>
                      <div className="h-2 w-2 rounded-full bg-transparent group-hover:bg-teal-500 transition-colors" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
